import type { Piece } from "./types";
import { bounding, rotate } from "./geometry";
import { canPlace, clearOcc, clearHighlight } from "./board";
import { state } from "./state";
import { slotsEl, highlightEl } from "../dom";
import { placeInGrid, returnToTray, rotatePieceInPlace, sizePiece, setPieceVisual } from "./pieces";
import { updateHUD } from "./flow";
import { sfxPack, sfxDiscard, sfxRotate } from "../audio/sfx";

export function onPointerDown(e: PointerEvent, p: Piece): void {
  if (e.button === 2) return; // right-click handled by contextmenu
  if (state.phase === "intro" || state.phase === "done") return;
  e.preventDefault();
  const rect = p.el.getBoundingClientRect();
  state.drag = {
    piece: p, pointerId: e.pointerId, started: false,
    startX: e.clientX, startY: e.clientY, lastX: e.clientX, lastY: e.clientY,
    grabDX: e.clientX - rect.left, grabDY: e.clientY - rect.top,
    fromGrid: p.location === "grid", prevPos: p.pos ? { ...p.pos } : null,
    target: null, canHere: false, outside: true,
  };
  window.addEventListener("pointermove", onPointerMove, { passive: false });
  window.addEventListener("pointerup", onPointerUp);
  window.addEventListener("pointercancel", onPointerUp);
}

function liftPiece(): void {
  const p = state.drag!.piece;
  if (p.location === "grid") clearOcc(p);
  p.el.classList.add("dragging");
  p.el.classList.remove("in-grid");
  p.el.style.position = "fixed";
  document.body.appendChild(p.el);
  sizePiece(p);
}

function positionGhost(x: number, y: number): void {
  const drag = state.drag!;
  drag.piece.el.style.left = x - drag.grabDX + "px";
  drag.piece.el.style.top = y - drag.grabDY + "px";
}

function updateHighlight(x: number, y: number): void {
  clearHighlight();
  const drag = state.drag!;
  const p = drag.piece;
  const cellPx = state.cellPx;
  const sr = slotsEl.getBoundingClientRect();
  const pieceLeft = x - drag.grabDX, pieceTop = y - drag.grabDY;
  const gc = Math.round((pieceLeft - sr.left) / cellPx);
  const gr = Math.round((pieceTop - sr.top) / cellPx);
  drag.target = { gr, gc };
  const { cols, rows } = state;
  let anyIn = false;
  for (const [r, c] of p.cells) {
    const R = gr + r, C = gc + c;
    if (R >= 0 && C >= 0 && R < rows && C < cols) anyIn = true;
  }
  drag.outside = !anyIn;
  const ok = canPlace(p.cells, gr, gc, p.id);
  drag.canHere = ok;
  if (!drag.outside) {
    for (const [r, c] of p.cells) {
      const R = gr + r, C = gc + c;
      if (R >= 0 && C >= 0 && R < rows && C < cols) {
        highlightEl.children[R * cols + C].classList.add(ok ? "valid" : "invalid");
      }
    }
  }
}

function onPointerMove(e: PointerEvent): void {
  const drag = state.drag;
  if (!drag) return;
  drag.lastX = e.clientX;
  drag.lastY = e.clientY;
  if (!drag.started) {
    if (Math.hypot(e.clientX - drag.startX, e.clientY - drag.startY) < 6) return;
    drag.started = true;
    liftPiece();
  }
  e.preventDefault();
  positionGhost(e.clientX, e.clientY);
  updateHighlight(e.clientX, e.clientY);
}

function onPointerUp(): void {
  const drag = state.drag;
  if (!drag) return;
  const p = drag.piece;
  window.removeEventListener("pointermove", onPointerMove);
  window.removeEventListener("pointerup", onPointerUp);
  window.removeEventListener("pointercancel", onPointerUp);

  if (!drag.started) { // a tap → rotate
    rotatePieceInPlace(p);
    sfxRotate();
    finishDrag();
    return;
  }

  let done = false;
  if (drag.target && canPlace(p.cells, drag.target.gr, drag.target.gc, p.id)) {
    placeInGrid(p, drag.target.gr, drag.target.gc, !drag.fromGrid);
    sfxPack();
    done = true;
  }
  if (!done && drag.fromGrid && !drag.outside && drag.prevPos && canPlace(p.cells, drag.prevPos.r, drag.prevPos.c, p.id)) {
    placeInGrid(p, drag.prevPos.r, drag.prevPos.c, false); // invalid move → snap back
    done = true;
  }
  if (!done) {
    returnToTray(p, drag.fromGrid, drag.prevPos); // dropped off-grid → leave behind / back to pile
    sfxDiscard();
  }
  finishDrag();
}

function finishDrag(): void {
  clearHighlight();
  if (state.drag) state.drag.piece.el.classList.remove("dragging");
  state.drag = null;
  updateHUD();
}

export function rotateDragging(): void {
  const drag = state.drag!;
  const p = drag.piece;
  p.cells = rotate(p.cells);
  setPieceVisual(p);
  const { h, w } = bounding(p.cells);
  drag.grabDX = (w * state.cellPx) / 2;
  drag.grabDY = (h * state.cellPx) / 2;
  positionGhost(drag.lastX, drag.lastY);
  updateHighlight(drag.lastX, drag.lastY);
}
